import React, { useState, useEffect, useRef } from "react";
import { useParams, useHistory } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import TextField from "@mui/material/TextField";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";

// Vision UI components
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import VuiBadge from "components/VuiBadge";

// Vision UI layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

import { api } from "../../api";
import { 
  IoGlobe, 
  IoRocketSharp, 
  IoRefresh, 
  IoCheckmarkCircle, 
  IoWarning, 
  IoTrashOutline, 
  IoArrowBack, 
  IoCodeSlash, 
  IoLink,
  IoTerminalOutline,
  IoCopyOutline,
  IoPlayOutline,
  IoTimeOutline,
  IoCheckmarkDoneCircleOutline
} from "react-icons/io5";

const darkInputSx = {
  backgroundColor: "#0b1437 !important",
  borderRadius: "8px",
  border: "1px solid rgba(255, 255, 255, 0.25) !important",
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#0b1437 !important",
    color: "#ffffff !important",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "transparent !important",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255, 255, 255, 0.4) !important",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#0075ff !important",
    },
  },
  "& .MuiInputBase-root": {
    backgroundColor: "#0b1437 !important",
    color: "#ffffff !important",
  },
  "& .MuiInputBase-input": {
    color: "#ffffff !important",
    backgroundColor: "transparent !important",
    fontSize: "0.875rem",
    "&::placeholder": {
      color: "rgba(255, 255, 255, 0.5) !important",
      opacity: "1 !important",
    },
  },
};

// Render-style syntax highlighter for console logs
function formatLogLine(line, idx) {
  let color = "#e2e8f0";
  let fontWeight = "normal";

  if (line.includes("[Git]")) {
    color = "#38bdf8"; // cyan
  } else if (line.includes("[Build]")) {
    color = "#c084fc"; // purple
  } else if (line.includes("[Notice]")) {
    color = "#fbbf24"; // amber
  } else if (line.includes("[Release]")) {
    color = "#60a5fa"; // blue
  } else if (line.includes("[SSL]")) {
    color = "#2dd4bf"; // teal
  } else if (line.includes("[Success]")) {
    color = "#34d399"; // green
    fontWeight = "bold";
  } else if (line.includes("[Error]") || line.includes("failed") || line.includes("Error:")) {
    color = "#f87171"; // red
    fontWeight = "bold";
  }

  return (
    <div key={idx} style={{ color, fontWeight, lineHeight: "1.55", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
      {line}
    </div>
  );
}

export default function SiteDetail() {
  const { id } = useParams();
  const history = useHistory();
  const terminalRef = useRef(null);

  const [site, setSite] = useState(null);
  const [deployments, setDeployments] = useState([]);
  const [selectedDepId, setSelectedDepId] = useState(null);
  const [logs, setLogs] = useState("Loading deployment logs...");
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [copiedLogs, setCopiedLogs] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);

  // Domains & Settings
  const [newDomain, setNewDomain] = useState("");
  const [domainError, setDomainError] = useState("");
  const [domainSuccess, setDomainSuccess] = useState("");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const [configData, setConfigData] = useState({
    branch: "main",
    rootDir: "/",
    buildCmd: "",
    publishDir: "dist",
  });

  const [verifyingDomainId, setVerifyingDomainId] = useState(null);
  const [copiedKey, setCopiedKey] = useState(null);

  const copyToClipboard = (text, key) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text);
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(null), 2000);
    }
  };

  const loadSiteData = async () => {
    try {
      const res = await api.getSite(id);
      if (res.success && res.data) {
        const siteObj = res.data.site || {};
        const domList = res.data.domains || siteObj.domains || [];
        setSite({
          ...siteObj,
          domains: domList,
        });
        const deps = res.data.deployments || [];
        setDeployments(deps);

        setConfigData({
          branch: res.data.site.branch || "main",
          rootDir: res.data.site.rootDir || "/",
          buildCmd: res.data.site.buildCmd || "",
          publishDir: res.data.site.publishDir || "dist",
        });

        // Auto-select latest deployment if not selected
        if (deps.length > 0 && !selectedDepId) {
          setSelectedDepId(deps[0].id);
        }
      }
    } catch (err) {
      console.error("Failed to load site details:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async (depId) => {
    if (!depId) return;
    setLoadingLogs(true);
    try {
      const res = await api.getDeploymentLogs(depId);
      if (res.success) {
        setLogs(res.data.logs || "No logs available yet for this deployment.");
      } else {
        setLogs("Could not retrieve logs for deployment #" + depId);
      }
    } catch (err) {
      setLogs("Error loading logs: " + err.message);
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadSiteData();
    const interval = setInterval(loadSiteData, 4000);
    return () => clearInterval(interval);
  }, [id]);

  useEffect(() => {
    if (selectedDepId) {
      loadLogs(selectedDepId);
    }
  }, [selectedDepId]);

  // Live polling of logs when deployment is running
  useEffect(() => {
    const activeDep = deployments.find((d) => d.id === selectedDepId);
    if (activeDep && (activeDep.status === "building" || activeDep.status === "queued")) {
      const pollInterval = setInterval(() => {
        loadLogs(selectedDepId);
      }, 2000);
      return () => clearInterval(pollInterval);
    }
  }, [selectedDepId, deployments]);

  // Auto-scroll to bottom of terminal
  useEffect(() => {
    if (autoScroll && terminalRef.current) {
      terminalRef.current.scrollTop = terminalRef.current.scrollHeight;
    }
  }, [logs, autoScroll]);

  const handleRedeploy = async () => {
    setActionLoading(true);
    try {
      const res = await api.redeploySite(id, configData.branch);
      if (res.success && res.data?.deploymentId) {
        setSelectedDepId(res.data.deploymentId);
      }
      await loadSiteData();
    } catch (err) {
      alert("Failed to trigger redeploy: " + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleRollback = async (depId) => {
    if (!window.confirm(`Are you sure you want to instantly roll back to Deployment #${depId}?`)) return;
    setActionLoading(true);
    try {
      await api.rollbackDeployment(depId);
      alert(`Rollback to Deployment #${depId} completed successfully!`);
      await loadSiteData();
    } catch (err) {
      alert("Rollback failed: " + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleCopyLogs = () => {
    navigator.clipboard.writeText(logs);
    setCopiedLogs(true);
    setTimeout(() => setCopiedLogs(false), 2000);
  };

  const handleUpdateConfig = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    setSaveSuccess(false);
    try {
      await api.updateSite(id, configData);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
      await loadSiteData();
    } catch (err) {
      alert("Failed to update config: " + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    setDomainError("");
    setDomainSuccess("");
    if (!newDomain.trim()) return;

    try {
      const res = await api.attachDomain(id, newDomain.trim());
      if (res.success) {
        setDomainSuccess(`Domain ${newDomain.trim()} added! Configure your DNS and click Verify.`);
        setNewDomain("");
        await loadSiteData();
      }
    } catch (err) {
      setDomainError(err.message || "Failed to attach domain");
    }
  };

  const handleVerifyDomain = async (domainId) => {
    setVerifyingDomainId(domainId);
    setDomainError("");
    setDomainSuccess("");
    try {
      const res = await api.verifyDomain(domainId);
      if (res.success && res.data?.verified) {
        setDomainSuccess(res.message || "Domain verified and SSL certificate provisioned successfully! HTTPS is active.");
      } else {
        setDomainError(res.error?.message || res.message || "Verification failed. Check DNS records and ensure CDN proxy is disabled.");
      }
      await loadSiteData();
    } catch (err) {
      setDomainError(err.message || "Verification error. Ensure DNS points to 192.255.149.52 and CDN proxy is removed.");
      await loadSiteData();
    } finally {
      setVerifyingDomainId(null);
    }
  };

  const handleDeleteDomain = async (domainId, domainName) => {
    if (!window.confirm(`Remove custom domain "${domainName}"?`)) return;
    try {
      await api.deleteDomain(domainId);
      loadSiteData();
    } catch (err) {
      alert("Failed to remove domain: " + err.message);
    }
  };

  const handleDeleteSite = async () => {
    if (!window.confirm(`DANGER: Are you sure you want to permanently delete "${site.name}"? This will delete all releases and immediately stop hosting.`)) {
      return;
    }
    try {
      await api.deleteSite(id);
      history.push("/sites");
    } catch (err) {
      alert("Failed to delete site: " + err.message);
    }
  };

  if (loading || !site) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox display="flex" justifyContent="center" alignItems="center" py={12}>
          <CircularProgress color="info" />
        </VuiBox>
      </DashboardLayout>
    );
  }

  const selectedDeployment = deployments.find((d) => d.id === selectedDepId) || deployments[0];
  const liveUrl = `https://${site.subdomain}.web.int.yt`;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        {/* Navigation Breadcrumb & Back */}
        <VuiBox mb={3} display="flex" alignItems="center" gap={1}>
          <VuiButton
            variant="text"
            color="white"
            size="small"
            onClick={() => history.push("/sites")}
            sx={{ pl: 0, textTransform: "none", color: "#8b9bb4" }}
          >
            <IoArrowBack size="16px" style={{ marginRight: "6px" }} /> Back to Sites
          </VuiButton>
        </VuiBox>

        {/* Hero Site Card */}
        <Card sx={{ mb: 3 }}>
          <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
            <VuiBox display="flex" alignItems="center" gap={2}>
              <VuiBox
                width="56px"
                height="56px"
                borderRadius="16px"
                bgColor="info"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <IoGlobe size="32px" color="#fff" />
              </VuiBox>
              <VuiBox>
                <VuiBox display="flex" alignItems="center" gap={1.5}>
                  <VuiTypography variant="h4" color="white" fontWeight="bold">
                    {site.name}
                  </VuiTypography>
                  <VuiBadge
                    color={site.status === "live" ? "success" : site.status === "building" ? "info" : "error"}
                    variant="standard"
                    size="sm"
                    badgeContent={site.status.toUpperCase()}
                  />
                </VuiBox>
                <VuiBox display="flex" alignItems="center" gap={2} mt={0.5} flexWrap="wrap">
                  <a href={liveUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                    <VuiTypography variant="button" color="info" fontWeight="bold" sx={{ "&:hover": { textDecoration: "underline" } }}>
                      {liveUrl} ↗
                    </VuiTypography>
                  </a>
                  <VuiTypography variant="caption" color="text" display="flex" alignItems="center" gap={0.5}>
                    <IoCodeSlash size="14px" />
                    {site.repoUrl}
                  </VuiTypography>
                  <VuiBadge color="dark" variant="gradient" size="xs" badgeContent={`branch: ${site.branch}`} />
                </VuiBox>
              </VuiBox>
            </VuiBox>

            {/* Quick Actions */}
            <VuiBox display="flex" alignItems="center" gap={1.5}>
              <VuiButton
                variant="gradient"
                color="info"
                onClick={handleRedeploy}
                disabled={actionLoading || site.status === "building"}
              >
                <IoRocketSharp style={{ marginRight: "6px" }} />
                {actionLoading || site.status === "building" ? "Deploying..." : "Redeploy"}
              </VuiButton>
              <VuiButton
                variant="outlined"
                color="white"
                component="a"
                href={liveUrl}
                target="_blank"
                rel="noreferrer"
              >
                Visit Site ↗
              </VuiButton>
              <VuiButton
                variant="text"
                color="error"
                onClick={handleDeleteSite}
                title="Delete Site"
              >
                <IoTrashOutline size="18px" />
              </VuiButton>
            </VuiBox>
          </VuiBox>
        </Card>

        {/* Deploy Logs Terminal (Render-Style) */}
        <Grid container spacing={3} mb={3}>
          <Grid item xs={12} lg={8}>
            <Card sx={{ height: "100%", p: 0, overflow: "hidden" }}>
              {/* Terminal Header Bar */}
              <VuiBox
                px={3}
                py={2}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={2}
                sx={{
                  backgroundColor: "#070c1e",
                  borderBottom: "1px solid rgba(255, 255, 255, 0.08)"
                }}
              >
                <VuiBox display="flex" alignItems="center" gap={1.5}>
                  <IoTerminalOutline size="20px" color="#0075ff" />
                  <VuiBox>
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      Deploy Logs &bull; Deployment #{selectedDeployment?.id || "N/A"}
                    </VuiTypography>
                    {selectedDeployment && (
                      <VuiTypography variant="caption" color="text" display="block">
                        {selectedDeployment.commitMessage || "Manual deployment"} ({selectedDeployment.commitSha ? selectedDeployment.commitSha.slice(0, 7) : "HEAD"})
                      </VuiTypography>
                    )}
                  </VuiBox>
                </VuiBox>

                <VuiBox display="flex" alignItems="center" gap={1}>
                  {selectedDeployment && (
                    <VuiBadge
                      color={selectedDeployment.status === "live" ? "success" : selectedDeployment.status === "building" ? "info" : "error"}
                      variant="standard"
                      size="sm"
                      badgeContent={selectedDeployment.status.toUpperCase()}
                    />
                  )}
                  <VuiButton
                    variant="text"
                    color="white"
                    size="small"
                    onClick={() => loadLogs(selectedDepId)}
                    disabled={loadingLogs}
                  >
                    <IoRefresh size="14px" style={{ marginRight: "4px" }} />
                    {loadingLogs ? "Refreshing..." : "Refresh"}
                  </VuiButton>
                  <VuiButton
                    variant="text"
                    color="white"
                    size="small"
                    onClick={handleCopyLogs}
                  >
                    <IoCopyOutline size="14px" style={{ marginRight: "4px" }} />
                    {copiedLogs ? "Copied!" : "Copy"}
                  </VuiButton>
                </VuiBox>
              </VuiBox>

              {/* Terminal Screen */}
              <VuiBox
                ref={terminalRef}
                p={2.5}
                sx={{
                  backgroundColor: "#040711",
                  fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', 'Monaco', monospace",
                  fontSize: "0.82rem",
                  minHeight: "380px",
                  maxHeight: "520px",
                  overflowY: "auto",
                  color: "#cbd5e1",
                  "&::-webkit-scrollbar": {
                    width: "8px",
                  },
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "rgba(255, 255, 255, 0.15)",
                    borderRadius: "4px",
                  },
                }}
              >
                {logs ? (
                  logs.split("\n").map((line, idx) => formatLogLine(line, idx))
                ) : (
                  <VuiTypography variant="caption" color="text">
                    No log output recorded for this build.
                  </VuiTypography>
                )}
              </VuiBox>

              {/* Terminal Footer Bar */}
              <VuiBox
                px={3}
                py={1.5}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{
                  backgroundColor: "#070c1e",
                  borderTop: "1px solid rgba(255, 255, 255, 0.08)"
                }}
              >
                <VuiTypography variant="caption" color="text">
                  {selectedDeployment?.durationMs ? `Duration: ${(selectedDeployment.durationMs / 1000).toFixed(2)}s` : "In progress / complete"} &bull; {selectedDeployment?.createdAt ? new Date(selectedDeployment.createdAt).toLocaleString() : ""}
                </VuiTypography>
                <label style={{ display: "flex", alignItems: "center", gap: "6px", cursor: "pointer", fontSize: "0.75rem", color: "#8b9bb4" }}>
                  <input
                    type="checkbox"
                    checked={autoScroll}
                    onChange={(e) => setAutoScroll(e.target.checked)}
                  />
                  Auto-scroll
                </label>
              </VuiBox>
            </Card>
          </Grid>

          {/* Deployment History Column (Render-style selector) */}
          <Grid item xs={12} lg={4}>
            <Card sx={{ height: "100%" }}>
              <VuiBox mb={2}>
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  Deploy History
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  Select any past deployment to inspect its logs or instantly roll back
                </VuiTypography>
              </VuiBox>

              <VuiBox display="flex" flexDirection="column" gap={1.5} sx={{ maxHeight: "480px", overflowY: "auto" }}>
                {deployments.length === 0 ? (
                  <VuiTypography variant="caption" color="text">
                    No deployments recorded yet.
                  </VuiTypography>
                ) : (
                  deployments.map((dep) => {
                    const isSelected = dep.id === selectedDepId;
                    return (
                      <VuiBox
                        key={dep.id}
                        p={1.5}
                        borderRadius="10px"
                        onClick={() => setSelectedDepId(dep.id)}
                        sx={{
                          cursor: "pointer",
                          backgroundColor: isSelected ? "rgba(0, 117, 255, 0.15)" : "rgba(255, 255, 255, 0.03)",
                          border: isSelected ? "1px solid #0075ff" : "1px solid rgba(255, 255, 255, 0.08)",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "rgba(0, 117, 255, 0.1)",
                          },
                        }}
                      >
                        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                          <VuiTypography variant="button" color={isSelected ? "info" : "white"} fontWeight="bold">
                            #{dep.id} &bull; {dep.branch}
                          </VuiTypography>
                          <VuiBadge
                            color={dep.status === "live" ? "success" : dep.status === "building" ? "info" : "error"}
                            variant="standard"
                            size="xs"
                            badgeContent={dep.status.toUpperCase()}
                          />
                        </VuiBox>
                        <VuiTypography variant="caption" color="text" display="block" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {dep.commitMessage || "Manual deployment"}
                        </VuiTypography>
                        <VuiBox display="flex" justifyContent="space-between" alignItems="center" mt={1}>
                          <VuiTypography variant="caption" color="text" fontSize="0.7rem">
                            {new Date(dep.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} &bull; {dep.durationMs ? `${(dep.durationMs / 1000).toFixed(1)}s` : "-"}
                          </VuiTypography>
                          {dep.status === "live" && (
                            <VuiButton
                              variant="text"
                              color="info"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRollback(dep.id);
                              }}
                              sx={{ p: 0, minHeight: "auto", textTransform: "none", fontSize: "0.7rem" }}
                            >
                              Rollback
                            </VuiButton>
                          )}
                        </VuiBox>
                      </VuiBox>
                    );
                  })
                )}
              </VuiBox>
            </Card>
          </Grid>
        </Grid>

        {/* Secondary Sections: Custom Domains & Site Settings */}
        <Grid container spacing={3}>
          {/* Custom Domains Section */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: "100%" }}>
              <VuiBox mb={2}>
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  Custom Domains &amp; SSL Certificate
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  Attach custom apex domains or subdomains with automated Let&apos;s Encrypt SSL
                </VuiTypography>
              </VuiBox>

              {/* Free Live Subdomain Info */}
              <VuiBox mb={2.5} p={2} sx={{ backgroundColor: "rgba(0, 117, 255, 0.08)", borderRadius: "10px", border: "1px solid rgba(0, 117, 255, 0.2)" }}>
                <VuiTypography variant="caption" color="text" display="block" mb={0.5}>
                  SYSTEM FREE SUBDOMAIN (HTTPS ACTIVE)
                </VuiTypography>
                <a href={liveUrl} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                  <VuiTypography variant="button" color="info" fontWeight="bold">
                    {site.subdomain}.web.int.yt ↗
                  </VuiTypography>
                </a>
              </VuiBox>

              {/* Proxy / Cloudflare Warning Notice */}
              <VuiBox mb={2.5} p={2} sx={{ backgroundColor: "rgba(255, 152, 0, 0.08)", borderRadius: "10px", border: "1px solid rgba(255, 152, 0, 0.35)" }}>
                <VuiBox display="flex" alignItems="flex-start" gap={1.5}>
                  <IoWarning size="22px" color="#ff9800" style={{ flexShrink: 0, marginTop: "2px" }} />
                  <VuiBox>
                    <VuiTypography variant="button" color="warning" fontWeight="bold" display="block">
                      Disable CDN Proxy Before Verification (Cloudflare / CDN)
                    </VuiTypography>
                    <VuiTypography variant="caption" color="text" display="block" mt={0.5} sx={{ lineHeight: 1.5 }}>
                      If your domain is hosted on <strong>Cloudflare</strong> or another CDN, change your DNS record proxy status from <strong>Proxied (Orange Cloud)</strong> to <strong>DNS Only (Grey Cloud)</strong>. Active proxies block automated Let&apos;s Encrypt verification and prevent SSL certificates from being issued.
                    </VuiTypography>
                  </VuiBox>
                </VuiBox>
              </VuiBox>

              {/* Copyable DNS Records Box */}
              <VuiBox mb={2.5} p={2} sx={{ backgroundColor: "#0b1437", borderRadius: "10px", border: "1px solid rgba(255, 255, 255, 0.1)" }}>
                <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={1.5}>
                  DNS CONFIGURATION (ADD AT YOUR DOMAIN REGISTRAR)
                </VuiTypography>

                {/* Option A: CNAME */}
                <VuiBox mb={1.5} p={1.5} sx={{ backgroundColor: "#040817", borderRadius: "8px", border: "1px solid rgba(0, 117, 255, 0.25)" }}>
                  <VuiTypography variant="caption" color="info" fontWeight="bold" display="block" mb={1}>
                    Option A: CNAME Record (Subdomains e.g. blog, app)
                  </VuiTypography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={3}>
                      <VuiTypography variant="caption" color="text" display="block">Type</VuiTypography>
                      <VuiTypography variant="button" color="white" fontWeight="bold">CNAME</VuiTypography>
                    </Grid>
                    <Grid item xs={4}>
                      <VuiTypography variant="caption" color="text" display="block">Name / Host</VuiTypography>
                      <VuiBox display="flex" alignItems="center" gap={0.5}>
                        <code style={{ color: "#00d4ff", fontSize: "0.8rem" }}>subdomain</code>
                        <VuiButton
                          variant="text"
                          color="info"
                          size="small"
                          sx={{ minWidth: "auto", p: 0.5 }}
                          onClick={() => copyToClipboard("subdomain", "cname-host")}
                        >
                          {copiedKey === "cname-host" ? <IoCheckmarkDoneCircleOutline size="15px" color="#00e676" /> : <IoCopyOutline size="15px" />}
                        </VuiButton>
                      </VuiBox>
                    </Grid>
                    <Grid item xs={5}>
                      <VuiTypography variant="caption" color="text" display="block">Target / Value</VuiTypography>
                      <VuiBox display="flex" alignItems="center" gap={0.5}>
                        <code style={{ color: "#00d4ff", fontSize: "0.8rem" }}>web.int.yt</code>
                        <VuiButton
                          variant="text"
                          color="info"
                          size="small"
                          sx={{ minWidth: "auto", p: 0.5 }}
                          onClick={() => copyToClipboard("web.int.yt", "cname-val")}
                        >
                          {copiedKey === "cname-val" ? <IoCheckmarkDoneCircleOutline size="15px" color="#00e676" /> : <IoCopyOutline size="15px" />}
                        </VuiButton>
                      </VuiBox>
                    </Grid>
                  </Grid>
                </VuiBox>

                {/* Option B: A Record */}
                <VuiBox p={1.5} sx={{ backgroundColor: "#040817", borderRadius: "8px", border: "1px solid rgba(0, 117, 255, 0.25)" }}>
                  <VuiTypography variant="caption" color="info" fontWeight="bold" display="block" mb={1}>
                    Option B: A Record (Root / Apex Domain e.g. domain.com)
                  </VuiTypography>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={3}>
                      <VuiTypography variant="caption" color="text" display="block">Type</VuiTypography>
                      <VuiTypography variant="button" color="white" fontWeight="bold">A</VuiTypography>
                    </Grid>
                    <Grid item xs={4}>
                      <VuiTypography variant="caption" color="text" display="block">Name / Host</VuiTypography>
                      <VuiBox display="flex" alignItems="center" gap={0.5}>
                        <code style={{ color: "#00d4ff", fontSize: "0.8rem" }}>@</code>
                        <VuiButton
                          variant="text"
                          color="info"
                          size="small"
                          sx={{ minWidth: "auto", p: 0.5 }}
                          onClick={() => copyToClipboard("@", "a-host")}
                        >
                          {copiedKey === "a-host" ? <IoCheckmarkDoneCircleOutline size="15px" color="#00e676" /> : <IoCopyOutline size="15px" />}
                        </VuiButton>
                      </VuiBox>
                    </Grid>
                    <Grid item xs={5}>
                      <VuiTypography variant="caption" color="text" display="block">Target / Value</VuiTypography>
                      <VuiBox display="flex" alignItems="center" gap={0.5}>
                        <code style={{ color: "#00d4ff", fontSize: "0.8rem" }}>192.255.149.52</code>
                        <VuiButton
                          variant="text"
                          color="info"
                          size="small"
                          sx={{ minWidth: "auto", p: 0.5 }}
                          onClick={() => copyToClipboard("192.255.149.52", "a-val")}
                        >
                          {copiedKey === "a-val" ? <IoCheckmarkDoneCircleOutline size="15px" color="#00e676" /> : <IoCopyOutline size="15px" />}
                        </VuiButton>
                      </VuiBox>
                    </Grid>
                  </Grid>
                </VuiBox>
              </VuiBox>

              {domainError && (
                <Alert severity="error" sx={{ mb: 2, backgroundColor: "rgba(244,67,54,0.15)", color: "#ff8a80" }}>
                  {domainError}
                </Alert>
              )}
              {domainSuccess && (
                <Alert severity="success" sx={{ mb: 2, backgroundColor: "rgba(0,230,118,0.15)", color: "#b9f6ca" }}>
                  {domainSuccess}
                </Alert>
              )}

              {/* Add Custom Domain Form */}
              <form onSubmit={handleAddDomain}>
                <VuiBox display="flex" gap={1.5} mb={2.5}>
                  <TextField
                    fullWidth
                    placeholder="e.g. portfolio.com or blog.mybrand.org"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    size="small"
                    sx={darkInputSx}
                  />
                  <VuiButton type="submit" variant="gradient" color="info" size="small" sx={{ whiteSpace: "nowrap" }}>
                    Add Domain
                  </VuiButton>
                </VuiBox>
              </form>

              {/* Attached Domains List */}
              <VuiBox mt={2}>
                <VuiTypography variant="button" color="white" fontWeight="bold" display="block" mb={1.5}>
                  Attached Domains ({site.domains ? site.domains.length : 0})
                </VuiTypography>

                {!site.domains || site.domains.length === 0 ? (
                  <VuiTypography variant="caption" color="text">
                    No custom domains attached yet.
                  </VuiTypography>
                ) : (
                  site.domains.map((dom) => {
                    const isSslActive = dom.sslStatus === "active" && dom.verified;
                    const isSslFailed = dom.sslStatus === "failed";
                    const isPending = !dom.verified;

                    return (
                      <VuiBox
                        key={dom.id}
                        p={2}
                        mb={1.5}
                        borderRadius="10px"
                        sx={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                      >
                        <VuiBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                          <VuiBox>
                            <VuiBox display="flex" alignItems="center" gap={1}>
                              {isSslActive ? (
                                <a href={`https://${dom.domain}`} target="_blank" rel="noreferrer" style={{ textDecoration: "none" }}>
                                  <VuiTypography variant="button" color="white" fontWeight="bold" sx={{ "&:hover": { color: "#0075ff" } }}>
                                    {dom.domain} ↗
                                  </VuiTypography>
                                </a>
                              ) : (
                                <VuiTypography variant="button" color="white" fontWeight="bold">
                                  {dom.domain}
                                </VuiTypography>
                              )}
                              {dom.isPrimary && (
                                <VuiBadge color="info" variant="standard" size="xs" badgeContent="PRIMARY" />
                              )}
                            </VuiBox>

                            <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                              {dom.isPrimary
                                ? "Default system site subdomain"
                                : "DNS: CNAME -> web.int.yt or A -> 192.255.149.52"}
                            </VuiTypography>
                          </VuiBox>

                          <VuiBox display="flex" alignItems="center" gap={1}>
                            {isSslActive && (
                              <VuiBadge color="success" variant="standard" size="sm" badgeContent="SSL ACTIVE & SECURE" />
                            )}
                            {isPending && (
                              <VuiBadge color="warning" variant="standard" size="sm" badgeContent="PENDING DNS" />
                            )}
                            {isSslFailed && (
                              <VuiBadge color="error" variant="standard" size="sm" badgeContent="SSL FAILED" />
                            )}

                            {/* Verify / Retry SSL Button */}
                            {!dom.isPrimary && (!isSslActive || isPending || isSslFailed) && (
                              <VuiButton
                                variant="gradient"
                                color="info"
                                size="small"
                                disabled={verifyingDomainId === dom.id}
                                onClick={() => handleVerifyDomain(dom.id)}
                              >
                                {verifyingDomainId === dom.id ? (
                                  <VuiBox display="flex" alignItems="center" gap={1}>
                                    <CircularProgress size={14} color="inherit" />
                                    <span>Verifying &amp; Issuing SSL...</span>
                                  </VuiBox>
                                ) : isSslFailed ? (
                                  "Retry SSL"
                                ) : (
                                  "Verify DNS & Issue SSL"
                                )}
                              </VuiButton>
                            )}

                            {/* Delete Button */}
                            {!dom.isPrimary && (
                              <VuiButton
                                variant="text"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteDomain(dom.id, dom.domain)}
                              >
                                <IoTrashOutline size="16px" />
                              </VuiButton>
                            )}
                          </VuiBox>
                        </VuiBox>

                        {/* SSL Error box if failed */}
                        {dom.sslError && (
                          <VuiBox mt={1.5} p={1.5} sx={{ backgroundColor: "rgba(244,67,54,0.1)", borderRadius: "8px", border: "1px solid rgba(244,67,54,0.25)" }}>
                            <VuiTypography variant="caption" color="error" display="block">
                              <strong>SSL Error:</strong> {dom.sslError}
                            </VuiTypography>
                            <VuiTypography variant="caption" color="text" display="block" mt={0.5}>
                              Tip: Ensure any CDN proxy (Cloudflare orange cloud) is turned <strong>OFF (DNS Only / Grey Cloud)</strong> before clicking Retry SSL.
                            </VuiTypography>
                          </VuiBox>
                        )}
                      </VuiBox>
                    );
                  })
                )}
              </VuiBox>
            </Card>
          </Grid>

          {/* Build Settings Section */}
          <Grid item xs={12} lg={6}>
            <Card sx={{ height: "100%" }}>
              <VuiBox mb={2}>
                <VuiTypography variant="h5" color="white" fontWeight="bold">
                  Build &amp; Directory Settings
                </VuiTypography>
                <VuiTypography variant="caption" color="text">
                  Configure repository branches, build commands, and output directories
                </VuiTypography>
              </VuiBox>

              {saveSuccess && (
                <Alert severity="success" sx={{ mb: 2, backgroundColor: "rgba(0,230,118,0.15)", color: "#b9f6ca" }}>
                  Settings updated successfully! Future deployments will use these settings.
                </Alert>
              )}

              <form onSubmit={handleUpdateConfig}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                      DEPLOY BRANCH
                    </VuiTypography>
                    <TextField
                      fullWidth
                      value={configData.branch}
                      onChange={(e) => setConfigData({ ...configData, branch: e.target.value })}
                      size="small"
                      sx={darkInputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                      ROOT DIRECTORY
                    </VuiTypography>
                    <TextField
                      fullWidth
                      value={configData.rootDir}
                      onChange={(e) => setConfigData({ ...configData, rootDir: e.target.value })}
                      size="small"
                      sx={darkInputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                      BUILD COMMAND
                    </VuiTypography>
                    <TextField
                      fullWidth
                      placeholder="Leave empty for plain HTML"
                      value={configData.buildCmd}
                      onChange={(e) => setConfigData({ ...configData, buildCmd: e.target.value })}
                      size="small"
                      sx={darkInputSx}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <VuiTypography variant="caption" color="white" fontWeight="bold" display="block" mb={0.5}>
                      PUBLISH DIRECTORY
                    </VuiTypography>
                    <TextField
                      fullWidth
                      placeholder="dist, build, public, or ."
                      value={configData.publishDir}
                      onChange={(e) => setConfigData({ ...configData, publishDir: e.target.value })}
                      size="small"
                      sx={darkInputSx}
                    />
                  </Grid>
                </Grid>

                <VuiBox mt={3} display="flex" justifyContent="flex-end">
                  <VuiButton type="submit" variant="gradient" color="info" disabled={actionLoading}>
                    {actionLoading ? "Saving..." : "Save Settings"}
                  </VuiButton>
                </VuiBox>
              </form>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}
